import { z } from "zod";

/**
 * Weekly opening hours for a venue (restaurants first), stored on
 * `activities.opening_hours`.
 *
 * Keys are short weekday ids rather than the long `DAYS_OF_WEEK` names used by
 * `open_days`: this is a separate, display-only field (when the doors are
 * open), not the booking calendar (which days take reservations).
 */
export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Weekday = (typeof WEEKDAYS)[number];

/** 24h "HH:mm". "24:00" is rejected: midnight is "00:00" with close < open. */
export const hhmmSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "expected HH:mm (24h)");

/**
 * One service: the doors open at `open` and close at `close`. A close earlier
 * than the open means the venue closes after midnight (18:30 to 02:00), so the
 * hours spill into the next calendar day. Equal times are ambiguous (closed?
 * 24h?) and are rejected rather than guessed.
 */
export const intervalSchema = z
  .object({ open: hhmmSchema, close: hhmmSchema })
  .refine((d) => d.open !== d.close, {
    message: "open and close must differ",
    path: ["close"],
  });
export type Interval = z.infer<typeof intervalSchema>;

/**
 * Up to two services a day, for venues that shut between lunch and dinner.
 * Two services must read in time order without overlapping, and only the
 * second may run past midnight: a first service crossing midnight would end
 * after the second one started.
 */
const servicesSchema = z
  .array(intervalSchema)
  .min(1)
  .max(2)
  .superRefine((services, ctx) => {
    const [first, second] = services;
    if (!first || !second) return;
    const firstOpen = toMinutes(first.open);
    const firstClose = toMinutes(first.close);
    if (firstClose < firstOpen) {
      ctx.addIssue({
        code: "custom",
        message: "the first service must end before midnight",
        path: [0, "close"],
      });
      return;
    }
    // Strictly after: two touching services are one service, and would make
    // the pill say "closing soon" in the middle of an unbroken evening.
    if (toMinutes(second.open) <= firstClose) {
      ctx.addIssue({
        code: "custom",
        message: "the second service must start after the first one ends",
        path: [1, "open"],
      });
    }
  });

/**
 * One day, or null when closed that day. The single `{ open, close }` object
 * is the original shape (every row written before two services existed) and
 * is still what we write for one service, so those rows never need rewriting.
 * The array form carries one or two services.
 */
export const dayHoursSchema = z.union([intervalSchema, servicesSchema]).nullable();
export type DayHours = z.infer<typeof dayHoursSchema>;

/**
 * A day's services as a list, whichever shape it is stored in: [] when closed,
 * in time order. It only reshapes; validate with the schema first.
 */
export function dayIntervals(day: DayHours | undefined): Interval[] {
  if (!day) return [];
  return Array.isArray(day) ? day : [day];
}

/**
 * The whole week. Null (or an absent column) means the hours are unknown, and
 * the site then shows nothing at all rather than a wrong "closed".
 */
export const openingHoursSchema = z
  .object({
    mon: dayHoursSchema,
    tue: dayHoursSchema,
    wed: dayHoursSchema,
    thu: dayHoursSchema,
    fri: dayHoursSchema,
    sat: dayHoursSchema,
    sun: dayHoursSchema,
  })
  .nullable();
export type OpeningHours = z.infer<typeof openingHoursSchema>;

/**
 * Reads a stored value defensively. The column is only validated on the API
 * and MCP write paths; a hand-written SQL update or a dump replay can still
 * leave a day without `close` or a time like "9h". One bad day makes the whole
 * week untrustworthy, so anything that fails the schema reads as unknown
 * (null): the page hides the hours instead of crashing or showing a wrong
 * "closed".
 */
export function parseOpeningHours(value: unknown): OpeningHours {
  const parsed = openingHoursSchema.safeParse(value ?? null);
  return parsed.success ? parsed.data : null;
}

export type OpeningState = "open" | "closing_soon" | "closed";

export interface OpeningStatus {
  state: OpeningState;
  /** When open or closing soon: the closing time. */
  until?: string;
  /** When closed: the next opening time, if the venue opens at all. */
  opensAt?: string;
  /** Weekday of that next opening (today's weekday when it opens later today). */
  opensDay?: Weekday;
  /**
   * Days until that opening: 0 later today, 1 tomorrow, up to 7 for "same
   * weekday next week". Disambiguates opensDay when it equals today.
   */
  opensInDays?: number;
}

/** Under this many minutes left, "open" becomes "closing_soon". */
const CLOSING_SOON_MINUTES = 60;
const MINUTES_PER_DAY = 24 * 60;

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":");
  return Number(h) * 60 + Number(m);
}

/**
 * One formatter per zone, built on first use. Building an Intl.DateTimeFormat
 * loads ICU data and costs far more than formatting with it, and opening
 * status is asked for every card in a list. In practice there is one zone,
 * Africa/Casablanca, so the map stays a single entry.
 */
const zoneFormatters = new Map<string, Intl.DateTimeFormat>();
function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = zoneFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    zoneFormatters.set(timeZone, formatter);
  }
  return formatter;
}

/**
 * Weekday and minute-of-day at `now` in `timeZone`. Uses Intl rather than
 * Date#getDay/getHours because those follow the device clock: the server runs
 * in UTC and visitors browse from every time zone, while the venue's hours
 * are Moroccan wall-clock time.
 */
function localWeekdayAndMinutes(now: Date, timeZone: string): { day: number; minutes: number } {
  if (Number.isNaN(now.getTime())) return { day: -1, minutes: 0 };
  const parts = zoneFormatter(timeZone).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const day = WEEKDAYS.indexOf(get("weekday").toLowerCase().slice(0, 3) as Weekday);
  // Some engines still print "24" for midnight despite h23; fold it back.
  const hour = Number(get("hour")) % 24;
  return { day, minutes: hour * 60 + Number(get("minute")) };
}

function openResult(until: string, minutesLeft: number): OpeningStatus {
  return { state: minutesLeft < CLOSING_SOON_MINUTES ? "closing_soon" : "open", until };
}

/**
 * Whether the venue is open at `now`, in the venue's time zone.
 *
 * Returns null when the hours are unknown, so callers render nothing.
 * Overnight intervals are honoured on both sides of midnight: with Friday
 * 18:30 to 02:00, Saturday 01:30 is still open (until 02:00). With two
 * services the gap between them reads as closed, pointing at the second
 * service's start today, and "closing soon" follows whichever service is on.
 */
export function openingStatus(
  stored: OpeningHours | undefined,
  now: Date,
  timeZone = "Africa/Casablanca",
): OpeningStatus | null {
  const hours = parseOpeningHours(stored);
  if (!hours) return null;
  const { day, minutes } = localWeekdayAndMinutes(now, timeZone);
  if (day < 0) return null;

  // Yesterday's last service may run past midnight into today. Only the last
  // can: the schema keeps a first service inside its own day.
  const yesterday = dayIntervals(hours[WEEKDAYS[(day + 6) % 7]!]).at(-1);
  if (yesterday) {
    const open = toMinutes(yesterday.open);
    const close = toMinutes(yesterday.close);
    if (close < open && minutes < close) return openResult(yesterday.close, close - minutes);
  }

  const today = dayIntervals(hours[WEEKDAYS[day]!]);
  for (const service of today) {
    const open = toMinutes(service.open);
    const close = toMinutes(service.close);
    if (close > open) {
      if (minutes >= open && minutes < close) return openResult(service.close, close - minutes);
    } else if (minutes >= open) {
      return openResult(service.close, MINUTES_PER_DAY - minutes + close);
    }
  }
  // Before the first service, or between lunch and dinner: the next start today.
  const laterToday = today.find((service) => toMinutes(service.open) > minutes);
  if (laterToday) {
    return { state: "closed", opensAt: laterToday.open, opensDay: WEEKDAYS[day], opensInDays: 0 };
  }

  // Up to 7 so a venue open one day a week points at next week's same day.
  for (let offset = 1; offset <= 7; offset++) {
    const weekday = WEEKDAYS[(day + offset) % 7]!;
    const next = dayIntervals(hours[weekday])[0];
    if (next) return { state: "closed", opensAt: next.open, opensDay: weekday, opensInDays: offset };
  }
  return { state: "closed" };
}
